import { useMemo, useState } from 'react'
import { endOfDay, startOfDay } from 'date-fns'
import {
  PiCurrencyDollar,
  PiTrophy,
  PiTrendUp,
  PiPercent,
} from 'react-icons/pi'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  StatsWidget,
  TrendsWidget,
  PipelineWidget,
  Card,
  CardHeader,
  CardTitle,
  CardAction,
  CardContent,
  Skeleton,
  type PerformanceStat,
  type PerformanceTrends,
  type LineChartData,
  type PeriodValue,
} from '@firstbite/ui'
import {
  getHomepageStats,
  getPerformanceStats,
  getPipelineValueStats,
  type HomepageStats,
  type PerformanceStats,
  type PipelineValueStats,
} from '../../services/recommendations_api'
import {
  formatCurrency,
  formatInteger,
  formatPercentage,
} from '../../utils/formatting'
import useApiService from '../../services/api'
import { usePreferences } from '../../context/preferences/PreferencesContext'
import {
  buildQuickSelects,
  defaultPeriodFor,
  resolveAppliedPeriod,
  toPipelineData,
} from './performanceDashboardData'

/**
 * PerformanceDashboardCardFbUi — the homepage "Performance" section rendered with
 * the `@firstbite/ui` performance widgets (Stats / Trends / Pipeline by stage),
 * replacing the hand-rolled Chart.js dashboard. It reuses the SAME data endpoints
 * (`getPerformanceStats`, `getHomepageStats`, `getPipelineValueStats`) and the
 * `userId` prop — no backend change — and just reshapes their responses into the
 * widgets' data props.
 *
 * Period: each card carries its own simplified picker (a range calendar + our
 * preset quick-selects, no fiscal Quarter/Half/Year modes) via `showModes={false}`.
 * All three share ONE applied period (`period` state) so any card's Apply reloads
 * every section. Picker *labels* are uncontrolled, so a change on one card won't
 * relabel the others' triggers, though their data stays in sync.
 */

interface PerformanceDashboardCardFbUiProps {
  /** Optional user ID for superusers to view another user's performance. */
  userId?: number
}

/**
 * First-load skeleton — mirrors the three-widget grid (Stats column + Trends and
 * Pipeline cards) with fb-ui `Skeleton` blocks so the layout doesn't shift when
 * the real data lands. Uses the same Card chrome, grid, and heights as the loaded
 * dashboard below.
 */
function PerformanceDashboardSkeleton() {
  const periodPill = <Skeleton className="h-9 w-36 rounded-lg" />
  return (
    <div className="grid grid-cols-1 gap-[1rem] lg:grid-cols-3">
      {/* Stats — four stacked KPI tiles. */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle>
            <Skeleton className="h-5 w-16" />
          </CardTitle>
          <CardAction>{periodPill}</CardAction>
        </CardHeader>
        <CardContent className="flex flex-col gap-[1rem]">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-2.5">
              <Skeleton className="size-9 shrink-0 rounded-xl" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-5 w-14" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Trends + Pipeline column. */}
      <div className="flex flex-col gap-[1rem] lg:col-span-2">
        <Card className="flex h-80 flex-col">
          <CardHeader className="pb-2">
            <CardTitle>
              <Skeleton className="h-5 w-20" />
            </CardTitle>
            <CardAction>{periodPill}</CardAction>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col gap-[1.25rem]">
            <div className="flex gap-6 border-b border-fbui-gray-200 pb-2">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-5 w-12" />
              <Skeleton className="h-5 w-16" />
            </div>
            <Skeleton className="min-h-0 flex-1 rounded-lg" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <Skeleton className="h-5 w-32" />
            </CardTitle>
            <CardAction>{periodPill}</CardAction>
          </CardHeader>
          <CardContent className="flex flex-col gap-[1rem]">
            <Skeleton className="h-3 w-full rounded-full" />
            <div className="grid grid-cols-2 gap-x-12 gap-y-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export function PerformanceDashboardCardFbUi({
  userId,
}: PerformanceDashboardCardFbUiProps) {
  // Picker quick-selects, built once for the picker UI. Their windows are frozen
  // at mount, so the actual apply goes through `resolveAppliedPeriod` (below),
  // which recomputes a preset's window from a fresh `now` at click time.
  const quickSelects = useMemo(() => buildQuickSelects(new Date()), [])

  // Default applied window ("Last 6 months"), computed once on mount.
  const [period, setPeriod] = useState<PeriodValue>(() =>
    defaultPeriodFor(new Date())
  )
  // Re-clicking a preset later (a tab left open across a day boundary) must not
  // apply the stale mount-time window — recompute presets from `now` on apply.
  const applyPeriod = (value: PeriodValue) =>
    setPeriod(resolveAppliedPeriod(value, new Date()))

  // Every applied period is a calendar range → the backend's `custom` window.
  const startDate = period.start
    ? startOfDay(period.start).toISOString()
    : undefined
  const endDate = period.end ? endOfDay(period.end).toISOString() : undefined
  const params = {
    userId,
    periodType: 'monthly' as const,
    range: 'custom' as const,
    startDate,
    endDate,
  }
  const key = [userId ?? 'self', startDate ?? null, endDate ?? null]
  const enabled = !!startDate && !!endDate

  const { data: stats, isLoading } = useQuery<PerformanceStats | null>({
    queryKey: ['performance-stats', ...key],
    queryFn: () => getPerformanceStats(params),
    enabled,
    placeholderData: keepPreviousData,
  })
  const { data: pipelineValueStats } = useQuery<PipelineValueStats | null>({
    queryKey: ['pipeline-value-stats', ...key],
    queryFn: () => getPipelineValueStats(params),
    enabled,
    placeholderData: keepPreviousData,
  })
  const { data: pipelineStats } = useQuery<HomepageStats | null>({
    queryKey: ['homepage-stats', userId ?? 'self'],
    queryFn: () => getHomepageStats(userId),
  })

  const { data: allStages } = useApiService().useGetSalesStages()
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  // API periods arrive newest-first; charts read oldest → left.
  const ordered = useMemo(() => [...(stats?.periods ?? [])].reverse(), [stats])
  const pipeOrdered = useMemo(
    () => [...(pipelineValueStats?.periods ?? [])].reverse(),
    [pipelineValueStats]
  )

  const performanceStats: PerformanceStat[] = stats
    ? [
        {
          key: 'deals-opened',
          label: 'Deals Opened',
          icon: <PiTrendUp />,
          value: formatInteger(stats.totals.deals_opened),
          help: 'Deals opened in the selected period.',
        },
        {
          key: 'deals-won',
          label: 'Deals Won',
          icon: <PiTrophy />,
          value: formatInteger(stats.totals.deals_won),
          help: 'Deals closed won in the selected period.',
        },
        {
          key: 'value-won',
          label: 'Value Won',
          icon: <PiCurrencyDollar />,
          value: formatCurrency(stats.totals.value_won),
          help: 'Annual value of deals won in the selected period.',
        },
        {
          key: 'win-rate',
          label: 'Win Rate',
          icon: <PiPercent />,
          value:
            stats.totals.overall_win_rate !== null
              ? `${formatPercentage(stats.totals.overall_win_rate)}%`
              : '-',
          help: 'Win rate — Won / (Won + Lost) — for the selected period.',
        },
      ]
    : []

  const activity: PerformanceTrends = {
    series: [
      { key: 'deals', label: 'Deals', color: 'primary' },
      { key: 'emails', label: 'Emails', color: 'sky' },
    ],
    points: ordered.map((p) => ({
      label: p.period_label,
      values: [p.deals_opened, p.emails_sent],
    })),
    formatValue: (n) => formatInteger(n),
  }

  const valueTrend: LineChartData = {
    labels: ordered.map((p) => p.period_label),
    series: [
      {
        key: 'won',
        label: 'Value Won',
        color: 'primary',
        values: ordered.map((p) => p.value_won),
      },
      {
        key: 'opened',
        label: 'Value Opened',
        color: 'sky',
        values: ordered.map((p) => p.value_opened),
      },
    ],
    leftFormat: (n) => formatCurrency(n),
  }

  const pipelineTrend: LineChartData = {
    labels: pipeOrdered.map((p) => p.period_label),
    series: [
      {
        key: 'unweighted',
        label: 'Pipeline value',
        color: 'indigo',
        values: pipeOrdered.map((p) => p.unweighted_value),
      },
      {
        key: 'weighted',
        label: 'Weighted value',
        color: 'violet',
        values: pipeOrdered.map((p) => p.weighted_value),
      },
      {
        key: 'count',
        label: 'Deals',
        color: 'primary',
        values: pipeOrdered.map((p) => p.deal_count),
        axis: 'right',
      },
    ],
    leftFormat: (n) => formatCurrency(n),
    rightFormat: (n) => formatInteger(n),
  }

  const pipeline = useMemo(
    () => toPipelineData(pipelineStats, allStages, customOrderEnabled),
    [pipelineStats, allStages, customOrderEnabled]
  )

  // First load (no data cached yet) → mirror the widgets' layout with skeletons
  // so nothing shifts when data lands. keepPreviousData keeps the prior data on
  // screen across period changes, so this only shows on the very first load.
  // On a hard error (not loading, still no data) fall through to `null`.
  if (!stats) return isLoading ? <PerformanceDashboardSkeleton /> : null

  // Shared across the three per-card pickers (see the component doc).
  const periodProps = {
    quickSelects,
    showModes: false,
    defaultPeriod: period,
    onPeriodApply: applyPeriod,
  }

  return (
    <div className="grid grid-cols-1 gap-[1rem] lg:grid-cols-3">
      <StatsWidget
        {...periodProps}
        stats={performanceStats}
        statColumns={1}
        className="max-w-none lg:col-span-1"
      />
      <div className="flex flex-col gap-[1rem] lg:col-span-2">
        <TrendsWidget
          {...periodProps}
          activity={activity}
          value={valueTrend}
          pipeline={pipelineTrend}
          className="h-80 max-w-none"
        />
        <PipelineWidget
          {...periodProps}
          pipeline={pipeline}
          className="max-w-none"
        />
      </div>
    </div>
  )
}
